import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SettingsIcon from "@mui/icons-material/Settings";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { DEFAULT_POINT_DETAIL_IDS } from "../utils/pointDetails";
import { MAP_TILE_LAYERS, MapTileId } from "@/constants/MapTiles";

/** A value the point details can show, as offered in the settings. */
export interface PointDetailOption {
  id: string;
  label: string;
  group: "engine" | "tellTale";
}

interface Props {
  tileId: MapTileId;
  onTileChange: (id: MapTileId) => void;
  /** What the shown vehicles reported during their trips, engine values first. */
  pointDetailOptions: PointDetailOption[];
  pointDetailIds: string[];
  onPointDetailIdsChange: (ids: string[]) => void;
  /** The options are still loading. */
  pointDetailsLoading?: boolean;
}

/** Space the button takes in the top right corner, so the legends can sit beside it. */
export const ROUTE_MAP_SETTINGS_WIDTH_PX = 56;

/**
 * The route map's settings: a gear in the top right corner that opens a small panel, as on the
 * map page.
 *
 * The basemap, and which values the point details show — picked from what the vehicles actually
 * reported during the shown trips. The top left corner belongs to Leaflet's zoom buttons, which the
 * row of basemap buttons that used to sit there covered.
 */
export function RouteMapSettings({
  tileId,
  onTileChange,
  pointDetailOptions,
  pointDetailIds,
  onPointDetailIdsChange,
  pointDetailsLoading,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const labelId = useId();
  const title = t("route.mapSettings");
  const basemapLabel = t("logbookAudit.map.tileSwitch");

  return (
    <>
      <Tooltip title={title} placement="left">
        <Paper
          elevation={2}
          sx={{ position: "absolute", right: 16, top: 16, zIndex: 1200, borderRadius: "50%", overflow: "hidden" }}
        >
          <IconButton
            onClick={() => setOpen(current => !current)}
            aria-label={title}
            aria-pressed={open}
            sx={{ width: 40, height: 40 }}
          >
            <SettingsIcon fontSize="small" color={open ? "primary" : "disabled"} />
          </IconButton>
        </Paper>
      </Tooltip>

      {open && (
        <Paper
          elevation={4}
          sx={{ position: "absolute", right: 16 + ROUTE_MAP_SETTINGS_WIDTH_PX, top: 16, zIndex: 1250, width: 340 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", px: 1.5, py: 0.5 }}>
            <Typography variant="subtitle2" sx={{ flex: 1 }}>
              {title}
            </Typography>
            <IconButton size="small" onClick={() => setOpen(false)} aria-label={t("route.closeMapSettings")}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ p: 1.5 }}>
            <Typography variant="overline" color="text.secondary" component="div" sx={{ mb: 1 }}>
              {basemapLabel}
            </Typography>
            <FormControl size="small" fullWidth>
              <InputLabel id={labelId}>{basemapLabel}</InputLabel>
              <Select
                labelId={labelId}
                label={basemapLabel}
                value={tileId}
                onChange={event => onTileChange(event.target.value as MapTileId)}
              >
                {MAP_TILE_LAYERS.map(layer => (
                  <MenuItem key={layer.id} value={layer.id}>
                    {t(`logbookAudit.map.tiles.${layer.id}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Divider />
          <Box sx={{ p: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Typography variant="overline" color="text.secondary" component="div" sx={{ flex: 1 }}>
                {t("route.pointDetails")}
              </Typography>
              <Tooltip title={t("route.pointDetailsReset")}>
                <IconButton size="small" onClick={() => onPointDetailIdsChange(DEFAULT_POINT_DETAIL_IDS)} aria-label={t("route.pointDetailsReset")}>
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Autocomplete
              multiple
              size="small"
              disableCloseOnSelect
              limitTags={4}
              loading={pointDetailsLoading}
              options={pointDetailOptions}
              groupBy={option => t(`route.pointDetailGroups.${option.group}`)}
              getOptionLabel={option => option.label}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={pointDetailIds.map(
                id => pointDetailOptions.find(option => option.id === id) ?? { id, label: id, group: "engine" as const },
              )}
              onChange={(_, next) => onPointDetailIdsChange(next.map(option => option.id))}
              renderInput={params => (
                <TextField
                  {...params}
                  label={t("route.pointDetailsChoose")}
                  helperText={t("route.pointDetailsHint")}
                />
              )}
              slotProps={{ listbox: { sx: { maxHeight: 320 } } }}
            />
          </Box>
        </Paper>
      )}
    </>
  );
}
